import {
    useBookmarkQueueQuery,
    useDeleteBookmarkMutation,
    useInsertBookmarkMutation
} from "graphql/generated";
import { fireEvent } from "lib/ga";
import { useCallback } from "react";

import { useAuth } from "./useAuth";
import { useContentDetailModal } from "./useContentDetailModal";

export const useBookmark = (
    content: Pick<Mix_Content, "id" | "bookmarked" | "originalurl">
) => {
    const { isLoggedIn, me, openSignInModal } = useAuth();
    const { saveContentUrlForRedirect } = useContentDetailModal();

    // 현재 큐 길이를 조회하여 새 북마크의 position 결정
    const { data: queueData } = useBookmarkQueueQuery({
        variables: { userId: me?.id ?? "" },
        skip: !me?.id,
        fetchPolicy: "cache-only"
    });

    const [insertBookmark] = useInsertBookmarkMutation({
        optimisticResponse: (variables) => {
            return {
                insert_mix_content_bookmark_one: {
                    id: content.id,
                    position: variables.position ?? null,
                    mix_content: {
                        id: variables.contentId,
                        bookmarked: true
                    }
                }
            };
        },
        onCompleted: () => {
            fireEvent({
                event: "content_bookmark",
                params: {
                    content_id: content.id,
                    content_url: content.originalurl,
                    value: true
                }
            });
        }
    });

    const [deleteBookmark] = useDeleteBookmarkMutation({
        optimisticResponse: (variables) => {
            return {
                delete_mix_content_bookmark: {
                    returning: [
                        {
                            id: content.id,
                            mix_content: {
                                id: variables.contentId,
                                bookmarked: false
                            }
                        }
                    ]
                }
            };
        },
        onCompleted: () => {
            fireEvent({
                event: "content_bookmark",
                params: {
                    content_id: content.id,
                    content_url: content.originalurl,
                    value: false
                }
            });
        }
    });

    const toggle = useCallback(() => {
        if (isLoggedIn) {
            if (!content.bookmarked) {
                // 큐의 맨 끝에 추가 (현재 큐 길이 = 새 position)
                const nextPosition =
                    queueData?.mix_content_bookmark?.length ?? 0;
                insertBookmark({
                    variables: {
                        contentId: content.id,
                        position: nextPosition
                    }
                });
            } else {
                deleteBookmark({
                    variables: {
                        contentId: content.id
                    }
                });
            }
        } else {
            // 로그인 후 콘텐츠 모달로 돌아오기 위해 URL 저장
            saveContentUrlForRedirect();
            openSignInModal();
        }
    }, [
        content.id,
        content.bookmarked,
        isLoggedIn,
        insertBookmark,
        deleteBookmark,
        openSignInModal,
        saveContentUrlForRedirect
    ]);

    return {
        toggle
    };
};
